import PropTypes from 'prop-types'
import {Form, Input} from 'antd'
import {useIntl} from 'react-intl'

const components = {
  search: Input.Search,
  password: Input.Password,
  textarea: Input.TextArea,
  default: Input,
}

const InputText = (props) => {
  const {
    label,
    name,
    placeholder,
    type,
    className = '',
    initialValue = '',
    style = null,
    hidden = false,
    maxLength = undefined,
    wrapperClass = '',
    pattern = undefined,
    isRequired = true,
    rules,
    readOnly = false,
    hideLabel = false,
    disabled = false,
    allowClear = type === 'search',
    onChange = () => {},
    onSubmit,
    errorHelp = false,
    errorMsg = '',
    fieldless = false,
  } = props
  const Tag = components[type] || components.default
  const intl = useIntl()

  // Test ID for E2E locators, derived from the form field name so it is stable
  // across refactors of this component. `name` may be an array for nested antd
  // form fields (PropTypes allows string | array); join it the same way the
  // `key` below already does so the two never disagree.
  const testId = Array.isArray(name) ? name.join('.') : name

  const validationRules =
    rules ??
    (() => {
      const generatedRules = []

      if (isRequired) {
        generatedRules.push({
          required: true,
          message:
            errorHelp === false
              ? ''
              : errorMsg.length > 0
                ? errorMsg
                : intl.formatMessage(
                    { id: 'validation.requiredField' },
                    { label }
                  ),
        })
      }

      if (pattern) {
        generatedRules.push({
          pattern: new RegExp(pattern),
          message:
            errorMsg.length > 0
              ? errorMsg
              : intl.formatMessage({ id: 'validation.invalidFormat' }),
        })
      }

      return generatedRules
    })()

  if (fieldless) {
    return (
      <Tag
        key={Array.isArray(name) ? name.join('.') : name}
        data-testid={testId}
        type={type}
        allowClear={allowClear}
        placeholder={placeholder ? placeholder : label}
        aria-label={label ? label : placeholder}
        readOnly={readOnly}
        disabled={disabled}
        pattern={pattern}
        maxLength={maxLength}
        className={`${wrapperClass} ${className}`}
        autoComplete="off"
        {...(style ? { style: style } : {})}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        {...(type === 'search' && onSubmit && { onSearch: (e) => onSubmit(e) })}
      />
    )
  }

  return (
    <Form.Item
      name={name}
      label={label ? label : ''}
      {...(initialValue ? { initialValue: initialValue } : {})}
      className={`${wrapperClass} ${hideLabel ? 'hide-label' : ''}`}
      style={{ marginBottom: 0 }}
      {...(hidden === true ? { hidden: true } : {})}
      rules={validationRules}
      {...(errorHelp === false ? { help: '' } : {})}
    >
      <Tag
        key={Array.isArray(name) ? name.join('.') : name}
        data-testid={testId}
        type={type}
        allowClear={allowClear}
        placeholder={placeholder ? placeholder : label}
        aria-label={label ? label : placeholder}
        readOnly={readOnly}
        disabled={disabled}
        pattern={pattern}
        maxLength={maxLength}
        className={className}
        autoComplete="off"
        {...(style ? { style: style } : {})}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        {...(type === 'search' && onSubmit && { onSearch: (e) => onSubmit(e) })}
      />
    </Form.Item>
  )
}

InputText.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  className: PropTypes.string,
  initialValue: PropTypes.string,
  style: PropTypes.object,
  hidden: PropTypes.bool,
  maxLength: PropTypes.number,
  wrapperClass: PropTypes.string,
  pattern: PropTypes.string,
  isRequired: PropTypes.bool,
  rules: PropTypes.array,
  readOnly: PropTypes.bool,
  hideLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  errorHelp: PropTypes.bool,
  errorMsg: PropTypes.string,
  fieldless: PropTypes.bool,
}

export default InputText
